import React, { useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import { Box, IconButton, Link, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'

import { IFilterMapped } from './DistributionChannelFilter'
import {
  EDistributionChannelType,
  IDistributionChannel,
} from '../../../../../types/distribution-channel'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../constants/global'
import { ImageLinks, LogoTypeSocial } from '../../../../../constants/image-link'
import { DistributionChannelFormModal } from './DistributionChannelFormModal'
import { axiosInstance } from '../../../../../hooks/useAxios'
import CircularLoading from '../../../../../components/CircularProgress'
import Pagination from '../../../../../components/Pagination'
import ImgTag from '../../../../../components/ImgTag'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { Tag } from '../../../../../components/Tag'

interface IDistributionChannelsTableProps {
  isLoading: boolean
  data: IDistributionChannel[]
  onUpdateDistributionChannelsSuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
}

const DistributionChannelsTable = ({
  isLoading,
  data,
  onUpdateDistributionChannelsSuccess,
  filter,
  onFilterChange,
  total,
}: IDistributionChannelsTableProps) => {
  const [isModalEditDistributionChannel, setIsModalEditDistributionChannel] =
    useState<boolean>(false)
  const [distributionChannelEdit, setDistributionChannelEdit] =
    useState<IDistributionChannel>()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const onHandleDeleteDistributionChannel = async (
    distributionChannel: IDistributionChannel,
  ) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.delete(
        `/distribution-channels/${distributionChannel.objectId}`,
      )
      message.success(`ลบช่องทางการขาย ${distributionChannel.name} สำเร็จ`)
      onUpdateDistributionChannelsSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }

  const columns: TableColumnsType<IDistributionChannel & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ช่องทาง',
      dataIndex: 'type',
      key: 'type',
      align: 'center' as const,
      width: 60,
      render: (type: EDistributionChannelType) => {
        return (
          <Box>
            <ImgTag
              imageUrl={
                ImageLinks[`LOGO_${type.toUpperCase()}` as LogoTypeSocial]
              }
              name={type as string}
              size={60}
            />
          </Box>
        )
      },
    },
    {
      title: 'ชื่อช่องทางการขาย',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (name: string, distributionChannel: IDistributionChannel) => (
        <Box
          sx={{
            minWidth: 200,
            textAlign: 'left',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {name}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {distributionChannel.type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Link
              href={distributionChannel.url}
              target="_blank"
              underline="hover"
            >
              {distributionChannel.url}
            </Link>
          </Typography>
        </Box>
      ),
    },
    {
      title: 'รหัสอ้างอิง',
      dataIndex: 'ref',
      key: 'ref',
      width: 160,
      align: 'center' as const,
      render: (ref: string) => (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {ref}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 160,
      align: 'center' as const,
      render: (isActive: boolean) => (
        <Box>
          <Tag color={isActive ? 'success' : 'error'}>
            {isActive ? 'ใช้งาน' : 'ปิดใช้งาน'}
          </Tag>
        </Box>
      ),
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) => (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      align: 'center' as const,
      render: (updatedAt: string) => (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {dayjs(updatedAt).format(ONLY_DATE_FORMAT)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(updatedAt).format(ONLY_TIME_FORMAT)}
          </Typography>
        </Box>
      ),
    },
    {
      dataIndex: '',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: undefined, distributionChannel: IDistributionChannel) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton
            onClick={() => {
              setIsModalEditDistributionChannel(true)
              setDistributionChannelEdit(distributionChannel)
            }}
          >
            <EditIcon />
          </IconButton>
          {/* <IconButton
            onClick={() =>
              onHandleDeleteDistributionChannel(distributionChannel)
            }
          >
            <DeleteIcon />
          </IconButton> */}
        </Box>
      ),
    },
  ]

  return (
    <>
      <DistributionChannelFormModal
        isOpen={isModalEditDistributionChannel}
        onUpdateDistributionChannelsSuccess={
          onUpdateDistributionChannelsSuccess
        }
        onClose={(visible: boolean) =>
          setIsModalEditDistributionChannel(visible)
        }
        distributionChannel={distributionChannelEdit}
      />
      {/* <CircularLoading isLoading={isLoading || isLoadingDelete} /> */}
      <Table
        loading={isLoading || isLoadingDelete}
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map(
          (distributionChannel: IDistributionChannel, index: number) => {
            return {
              ...distributionChannel,
              key: index,
              numberRow:
                index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
            }
          },
        )}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          onFilterChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default DistributionChannelsTable
